import { useEffect, useRef, useState } from "react";
import type { StreamEvent } from "./types";

type Listener = (ev: StreamEvent) => void;

interface SseBus {
  source: EventSource | null;
  listeners: Set<Listener>;
  alive: boolean;
  lastEventAt: number;
}

const bus: SseBus = {
  source: null,
  listeners: new Set(),
  alive: false,
  lastEventAt: 0,
};

function open(): void {
  if (bus.source) return;
  try {
    const es = new EventSource("/api/stream");
    bus.source = es;
    bus.alive = false;

    const dispatch = (raw: MessageEvent) => {
      bus.alive = true;
      bus.lastEventAt = Date.now();
      try {
        const ev = JSON.parse(raw.data) as StreamEvent;
        for (const fn of bus.listeners) fn(ev);
      } catch { /* skip bad packet */ }
    };

    // We subscribe to all events Hono streams (each has its own event name)
    for (const t of ["hello", "log", "request", "bus", "spawn", "status", "config", "ping"]) {
      es.addEventListener(t, dispatch);
    }
    es.addEventListener("error", () => {
      bus.alive = false;
      // EventSource will auto-reconnect; nothing else to do
    });
  } catch {
    bus.source = null;
  }
}

function close(): void {
  bus.source?.close();
  bus.source = null;
  bus.alive = false;
}

export function subscribeStream(fn: Listener): () => void {
  bus.listeners.add(fn);
  open();
  return () => {
    bus.listeners.delete(fn);
    if (bus.listeners.size === 0) close();
  };
}

export function useStreamEvent(filter: StreamEvent["type"] | StreamEvent["type"][], fn: (ev: StreamEvent) => void): void {
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  useEffect(() => {
    const types = new Set(Array.isArray(filter) ? filter : [filter]);
    return subscribeStream((ev) => {
      if (types.has(ev.type)) fnRef.current(ev);
    });
  }, [Array.isArray(filter) ? filter.join(",") : filter]);
}

export function useStreamHealth(): { alive: boolean; lastEventAt: number } {
  const [alive, setAlive] = useState(bus.alive);
  const [lastEventAt, setLastEventAt] = useState(bus.lastEventAt);
  useEffect(() => {
    const id = setInterval(() => {
      setAlive(bus.alive);
      setLastEventAt(bus.lastEventAt);
    }, 1000);
    return () => clearInterval(id);
  }, []);
  return { alive, lastEventAt };
}

// Ensure stream is opened even with no subscribers, so the topbar pulse works
export function ensureStreamOpen(): void {
  open();
}
